import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export const HomePage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    document.title = 'Custom Group — AI Advisory';
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#f5f5f5]">
      {/* Sticky Nav */}
      <nav className="bg-[#0a0a0a] border-b border-[#222222] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <img src="/logo.svg" alt="Custom Group.Tech — We think the future, your future" className="h-9 w-auto" />
            </Link>

            {/* Desktop links */}
            <div className="hidden md:flex items-center gap-10 text-sm font-medium text-[#888888]">
              <Link to="/blog" className="hover:text-[#f5f5f5] transition-colors">Insights</Link>
              <a href="#approach" className="hover:text-[#f5f5f5] transition-colors">Our Approach</a>
              <a href="#access" className="hover:text-[#f5f5f5] transition-colors">Advisory Access</a>
              <Link
                to="/apply"
                className="px-5 py-2 text-sm font-semibold text-white transition-all hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #E8651A 0%, #F5922B 100%)' }}
              >
                Apply for Access
              </Link>
            </div>

            {/* Mobile hamburger */}
            <button
              className="md:hidden p-2 text-[#888888] hover:text-[#f5f5f5]"
              onClick={() => setMobileMenuOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-[#222222] py-6 flex flex-col gap-4 text-sm font-medium">
              <Link to="/blog" className="text-[#888888] hover:text-[#f5f5f5]" onClick={() => setMobileMenuOpen(false)}>Insights</Link>
              <a href="#approach" className="text-[#888888] hover:text-[#f5f5f5]" onClick={() => setMobileMenuOpen(false)}>Our Approach</a>
              <a href="#access" className="text-[#888888] hover:text-[#f5f5f5]" onClick={() => setMobileMenuOpen(false)}>Advisory Access</a>
              <Link
                to="/apply"
                className="bg-[#E8651A] text-[#0a0a0a] px-5 py-3 text-sm font-semibold text-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                Apply for Access
              </Link>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="min-h-[90vh] flex items-center border-b border-[#222222] relative overflow-hidden">
        {/* Subtle orange radial glow — top right */}
        <div
          className="absolute top-0 right-0 w-[600px] h-[600px] pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at top right, rgba(232,101,26,0.12) 0%, transparent 65%)',
          }}
        />
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-32 relative z-10">
          {/* Orange accent rule */}
          <div className="flex items-center gap-3 mb-10">
            <div style={{ width: 36, height: 3, background: 'linear-gradient(90deg, #E8651A, #F5922B)' }} />
            <span className="text-xs uppercase tracking-widest text-[#E8651A] font-semibold">AI Advisory · Custom Group</span>
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight tracking-tight mb-8 max-w-4xl">
            AI That Delivers Measurable Economic Value — Securely, Inside Your Business
          </h1>
          <p className="text-xl text-[#888888] max-w-2xl mb-6 leading-relaxed">
            We advise leadership teams on where and how to deploy AI for maximum return — without exposing data or creating unpredictable cost structures.
          </p>
          <p className="text-sm text-[#E8651A] mb-12 tracking-wide">
            We accept a limited number of mandates each quarter. Engagements begin at $25,000.
            Access is by application only.
          </p>
          <Link
            to="/apply"
            className="inline-block px-8 py-4 text-base font-semibold text-white transition-all hover:opacity-90"
            style={{ background: 'linear-gradient(135deg, #E8651A 0%, #F5922B 100%)' }}
          >
            Apply for Advisory Access
          </Link>
        </div>
      </section>

      {/* Engagements Section */}
      <section className="border-b border-[#222222]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <h2 className="text-3xl font-bold mb-16 tracking-tight">Engagements</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#222222]">
            {/* Card 1 */}
            <div className="bg-[#111111] p-10">
              <p className="text-xs text-[#E8651A] tracking-widest uppercase mb-6">From $7,500</p>
              <h3 className="text-xl font-bold mb-4">AI Strategy Intensive</h3>
              <p className="text-[#888888] text-sm leading-relaxed mb-8">
                A working session with your leadership team. We map economic opportunity, identify AI investment priorities, and surface infrastructure risks.
              </p>
              <div className="border-t border-[#222222] pt-6">
                <p className="text-xs text-[#888888] uppercase tracking-widest mb-2">Output</p>
                <p className="text-sm text-[#f5f5f5]">Economic opportunity map · Investment priorities · Risk considerations</p>
              </div>
            </div>

            {/* Card 2 */}
            <div className="bg-[#111111] p-10">
              <p className="text-xs text-[#E8651A] tracking-widest uppercase mb-6">$25,000 – $75,000</p>
              <h3 className="text-xl font-bold mb-4">AI ROI Mandate</h3>
              <p className="text-[#888888] text-sm leading-relaxed mb-8">
                Validated use cases, ROI modelling, selectively implemented pilots, and board-level clarity on next steps.
              </p>
              <div className="border-t border-[#222222] pt-6">
                <p className="text-xs text-[#888888] uppercase tracking-widest mb-2">Output</p>
                <p className="text-sm text-[#f5f5f5]">Validated use cases · ROI models · Pilot systems · Board report</p>
              </div>
            </div>

            {/* Card 3 */}
            <div className="bg-[#111111] p-10">
              <p className="text-xs text-[#E8651A] tracking-widest uppercase mb-6">$75,000 – $250,000+</p>
              <h3 className="text-xl font-bold mb-4">AI Transformation Advisory</h3>
              <p className="text-[#888888] text-sm leading-relaxed mb-8">
                Multi-phase deployment strategy, internal capability design, and ongoing senior advisory.
              </p>
              <div className="border-t border-[#222222] pt-6">
                <p className="text-xs text-[#888888] uppercase tracking-widest mb-2">Output</p>
                <p className="text-sm text-[#f5f5f5]">Deployment strategy · Capability design · Ongoing advisory</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Guarantee Section */}
      <section className="border-b border-[#222222]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-24">
          <div className="flex items-center gap-3 mb-8">
            <div style={{ width: 32, height: 3, background: 'linear-gradient(90deg, #E8651A, #F5922B)' }} />
            <span className="text-xs uppercase tracking-widest text-[#E8651A] font-semibold">Our Guarantee</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold mb-6 tracking-tight">Measurable Return — Or Your Money Back</h2>
          <p className="text-[#888888] text-lg leading-relaxed mb-8">
            We guarantee measurable operational improvement from every engagement. If you implement our 
            recommendations as specified and do not achieve documented time savings, cost reduction, or 
            revenue increase within 90 days, we refund your advisory fee.
          </p>
          <div className="bg-[#111111] border border-[#222222] p-8 mb-6">
            <h3 className="text-lg font-bold mb-4">What This Requires From You</h3>
            <ul className="space-y-3 text-[#888888] text-sm">
              <li className="flex items-start gap-3">
                <span className="text-[#E8651A] font-bold shrink-0">1.</span>
                <span>Implementation completed within 6 months of engagement delivery</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#E8651A] font-bold shrink-0">2.</span>
                <span>Deployment follows our technical specifications</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#E8651A] font-bold shrink-0">3.</span>
                <span>Internal process changes completed as outlined</span>
              </li>
            </ul>
          </div>
          <div className="bg-[#111111] border border-[#222222] p-8 mb-8">
            <h3 className="text-lg font-bold mb-4">What We Measure</h3>
            <ul className="space-y-3 text-[#888888] text-sm">
              <li className="flex items-start gap-3">
                <svg className="w-5 h-5 text-[#E8651A] shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span><strong className="text-[#f5f5f5]">Time saved</strong> — manual process hours reclaimed</span>
              </li>
              <li className="flex items-start gap-3">
                <svg className="w-5 h-5 text-[#E8651A] shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span><strong className="text-[#f5f5f5]">Costs reduced</strong> — vendor fees, operational overhead</span>
              </li>
              <li className="flex items-start gap-3">
                <svg className="w-5 h-5 text-[#E8651A] shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span><strong className="text-[#f5f5f5]">Revenue increased</strong> — faster onboarding, higher throughput</span>
              </li>
            </ul>
          </div>
          <p className="text-[#555555] text-sm leading-relaxed mb-4">
            We don't guarantee specific percentage gains — every business is different. But we do guarantee 
            that you'll see measurable, documented improvement. If you don't, you don't pay.
          </p>
          <p className="text-[#888888] text-sm italic">
            This isn't a promise we make lightly. It's a filter. If we don't believe we can deliver measurable 
            return in your context, we won't take the engagement. 
            <Link to="/terms" className="text-[#E8651A] hover:underline ml-1">Read full guarantee terms →</Link>
          </p>
        </div>
      </section>

      {/* Insights Section */}
      <section className="border-b border-[#222222]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <h2 className="text-3xl font-bold mb-16 tracking-tight">Insights</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#222222]">
            <div className="bg-[#111111] p-10">
              <p className="text-xs text-[#888888] uppercase tracking-widest mb-6">AI &amp; Automation</p>
              <h3 className="text-lg font-bold mb-4 leading-snug">Where AI Actually Creates ROI in 2026</h3>
              <Link
                to="/blog/where-ai-creates-roi-2026"
                className="text-sm text-[#E8651A] hover:text-[#C9520E] transition-colors"
              >
                Read →
              </Link>
            </div>
            <div className="bg-[#111111] p-10">
              <p className="text-xs text-[#888888] uppercase tracking-widest mb-6">AI &amp; Automation</p>
              <h3 className="text-lg font-bold mb-4 leading-snug">Why Most AI Investments Fail</h3>
              <Link
                to="/blog/why-most-ai-investments-fail"
                className="text-sm text-[#E8651A] hover:text-[#C9520E] transition-colors"
              >
                Read →
              </Link>
            </div>
            <div className="bg-[#111111] p-10">
              <p className="text-xs text-[#888888] uppercase tracking-widest mb-6">AI &amp; Automation</p>
              <h3 className="text-lg font-bold mb-4 leading-snug">The Cost of API Dependency vs Local Deployment</h3>
              <Link
                to="/blog/api-dependency-vs-local-deployment"
                className="text-sm text-[#E8651A] hover:text-[#C9520E] transition-colors"
              >
                Read →
              </Link>
            </div>
            <div className="bg-[#111111] p-10">
              <p className="text-xs text-[#888888] uppercase tracking-widest mb-6">AI &amp; Strategy</p>
              <h3 className="text-lg font-bold mb-4 leading-snug">Your AI Vendor Is Quietly Draining Your Margins</h3>
              <Link
                to="/blog/ai-vendor-margin-drain"
                className="text-sm text-[#E8651A] hover:text-[#C9520E] transition-colors"
              >
                Read →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach Section */}
      <section id="approach" className="border-b border-[#222222]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <h2 className="text-3xl font-bold mb-16 tracking-tight">Our Approach</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-16">
            <div>
              <h3 className="text-base font-bold mb-4">Economic-first, not technology-first</h3>
              <p className="text-[#888888] text-sm leading-relaxed">
                We begin with the question: where does AI generate measurable value for this organisation? Technology follows strategy.
              </p>
            </div>
            <div>
              <h3 className="text-base font-bold mb-4">Security-first architecture</h3>
              <p className="text-[#888888] text-sm leading-relaxed">
                We design for local deployment and data sovereignty. Your data does not leave your infrastructure.
              </p>
            </div>
            <div>
              <h3 className="text-base font-bold mb-4">Selective engagement model</h3>
              <p className="text-[#888888] text-sm leading-relaxed">
                We limit the number of active mandates to ensure senior attention on every engagement.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Client Profile Section */}
      <section className="border-b border-[#222222]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <h2 className="text-3xl font-bold mb-16 tracking-tight">We Typically Work With</h2>
          <div className="max-w-2xl">
            <div className="space-y-4 mb-12">
              <p className="text-[#f5f5f5]">— Mid-to-large organisations with a clear mandate for efficiency, scale, or cost reduction</p>
              <p className="text-[#f5f5f5]">— Leadership teams: CEO, COO, CTO</p>
              <p className="text-[#f5f5f5]">— Organisations where AI ROI is a board-level priority</p>
            </div>
            <div className="border-t border-[#222222] pt-8">
              <p className="text-[#888888] text-sm italic">
                We are not a fit for exploratory or low-budget initiatives.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Advisory Retainer Section */}
      <section className="border-b border-[#222222]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <h2 className="text-3xl font-bold mb-4 tracking-tight">Advisory Retainer Hours</h2>
          <p className="text-[#888888] mb-12 max-w-2xl leading-relaxed">
            For leadership teams that require direct senior advisory access — outside of a structured engagement.
            Purchased as a pre-committed block, consumed within 30 days.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#222222] max-w-3xl">
            <div className="bg-[#111111] p-10">
              <p className="text-xs text-[#E8651A] tracking-widest uppercase mb-4">4-Hour Block</p>
              <p className="text-3xl font-bold mb-2">$10,000</p>
              <p className="text-sm text-[#888888] leading-relaxed mt-4">
                Four hours of direct advisory time with senior leadership. Used for strategic reviews,
                decision validation, or team briefings. Expires 30 days from purchase.
              </p>
            </div>
            <div className="bg-[#111111] p-10">
              <p className="text-xs text-[#E8651A] tracking-widest uppercase mb-4">8-Hour Block</p>
              <p className="text-3xl font-bold mb-2">$18,000</p>
              <p className="text-sm text-[#888888] leading-relaxed mt-4">
                Eight hours of direct advisory time. Suited to ongoing strategic dialogue,
                multiple stakeholder sessions, or intensive problem-framing over a month.
              </p>
            </div>
          </div>
          <p className="text-xs text-[#555555] mt-6">Retainer hours are available to qualified organisations only. Apply below.</p>
        </div>
      </section>

      {/* Advisory Access Section */}
      <section id="access" className="border-b border-[#222222]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="max-w-2xl">
            <h2 className="text-3xl font-bold mb-6 tracking-tight">Advisory Access</h2>
            <p className="text-[#888888] mb-12 leading-relaxed">
              Every engagement begins with an application. We review each submission and respond
              within 2 business days — only if there is a clear potential fit.
            </p>
            <div className="space-y-6 mb-12">
              <div className="flex items-start gap-4">
                <span className="text-[#E8651A] text-sm font-bold w-6 shrink-0 mt-0.5">1</span>
                <div>
                  <p className="text-[#f5f5f5] text-sm font-medium">Application</p>
                  <p className="text-[#555555] text-xs mt-1">Submit your mandate, organisation profile, and decision timeline.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="text-[#E8651A] text-sm font-bold w-6 shrink-0 mt-0.5">2</span>
                <div>
                  <p className="text-[#f5f5f5] text-sm font-medium">Qualification</p>
                  <p className="text-[#555555] text-xs mt-1">If there is a fit, we schedule a 30-minute qualification call to confirm scope and capacity.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="text-[#E8651A] text-sm font-bold w-6 shrink-0 mt-0.5">3</span>
                <div>
                  <p className="text-[#f5f5f5] text-sm font-medium">Paid Strategy Session</p>
                  <p className="text-[#555555] text-xs mt-1">Initial engagements begin at $7,500. This is a working session, not a sales call.</p>
                </div>
              </div>
            </div>
            <Link
              to="/apply"
              className="inline-block bg-[#E8651A] text-[#0a0a0a] px-8 py-4 text-base font-semibold hover:bg-[#C9520E] transition-colors mb-4"
            >
              Apply for Advisory Access
            </Link>
            <p className="text-xs text-[#555555] mt-4">Not every application proceeds. That is intentional.</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#222222] py-16 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start gap-8">
          <div>
            <img src="/logo.svg" alt="Custom Group.Tech" className="h-10 w-auto mb-3" />
            <p className="text-xs text-[#888888] italic mb-1">We think the future, your future</p>
            <p className="text-xs text-[#555555]">Ballito, South Africa · © 2026 Custom Group.Tech. All rights reserved.</p>
          </div>
          <div className="flex flex-wrap gap-8 text-sm text-[#888888]">
            <Link to="/blog" className="hover:text-[#f5f5f5] transition-colors">Insights</Link>
            <Link to="/founder" className="hover:text-[#f5f5f5] transition-colors">About</Link>
            <Link to="/apply" className="hover:text-[#f5f5f5] transition-colors">Apply</Link>
            <Link to="/terms" className="hover:text-[#f5f5f5] transition-colors">Terms</Link>
            <a
              href="https://www.linkedin.com/in/douglasbothma/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#f5f5f5] transition-colors"
            >
              LinkedIn
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};
